import * as React from 'react';
import { Grid, Button } from '../../../common';
import StandardModal from '../../../common/modals/StandardModal';
import '../../../../components/PortOpeningRequests/components/porRule/RuleModal.css';
import TextAreaField from '../../../common/form-controls/TextareaField';
import { ToggleField } from '../../../common/';
import TextInputField from '../../../common/form-controls/TextInputField';
import { AbstractRulesForm, IRuleStateForm, RulesFormProps } from './OnPremAbstractObjectsForm';
import { RuleOperation } from '../../../../models/enums/POR/RuleOperation';
import SelectField from '../../../common/form-controls/SelectField';
import ObjectLookup from '../../../common/form-controls/Lookups/ObjectLookup';
import OnPremPortOpeningObject from '../../../../models/entities/OnPremPortOpeningObject';

interface RuleModalSubToOnPremProps extends RulesFormProps {
	//porDirection: PortOpeningRequestsDirection;
}

interface SubToOnPremRuleStateForm extends IRuleStateForm {
    name: string;
    nestedObjectsList: OnPremPortOpeningObject[];
}

interface RuleModalSubToOnPremState {
	stateForm: SubToOnPremRuleStateForm;
}

export default class RuleModalSubToOnPrem extends AbstractRulesForm<
	RuleModalSubToOnPremProps,
	RuleModalSubToOnPremState
> {

	constructor(props: RuleModalSubToOnPremProps) {
		super(props);
		this.state = {
			stateForm: this.props.isFromHistory ? this.getRuleCopy() : this.getDefaultForm(),
		};

		this.initFormModel();
	}

	isURLValid = (url) => /^(((http(s)?:\/\/.)?(www\.)?)|(\*\.)?)[a-zA-Z0-9]+[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-z]{2,9}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/.test(url);

	initFormModel() {
		super.initFormModel();
		this.formModel.addField('name', {
			getValue: () => this.state.stateForm.name,
			validation: {
				required: true,
				rules:
					this.state.stateForm.operation?.name === RuleOperation.Add.name &&
					[
						{
							assert: () => this.state.stateForm.name?.length <= 74,
							message: 'Name cannot be longer than 74 characters.',
						},
						{
							assert: () => !this.state.stateForm.name?.includes(' '),
							message: 'Name cannot contain spaces.',
						},
						{
							assert: () => /^[a-zA-Z0-9](?:[a-zA-Z0-9_.-]*[a-zA-Z0-9])?$/.test(this.state.stateForm.name ?? ''),
							message: 'Name must begin with a letter, end with a letter or number, and may contain only letters, numbers, underscores, periods or dashes.',
						}
					]
			}
		});

		this.formModel.addField('objectIPs', {
			getValue: () => this.state.stateForm.objectIPs,
			validation: {
				required: () => !(this.state.stateForm.objectURLsFQDNs?.trim().length > 0 || this.state.stateForm.nestedObjects?.trim().length),
				rules: [
					{
						assert: () => !this.hasEmptyValues(this.state.stateForm.objectIPs),
						message: 'Empty values are not allowed.'
					}
				]
			}
		});

		this.formModel.fields.objectIPs.validation.rules.push(
			this.IpInputValidationRule({
				onlyPrivateAddresses: false
			})
		);

		/* this.formModel.fields.destinationIPs.validation.rules.push(
			this.IpInputValidationRule({
				onlyPrivateAddresses: false
			})
		);
 */
		/* this.formModel.fields.destinationIPs.validation.required =
			() => !(this.state.stateForm.objectURLsFQDNs?.trim().length > 0);

		this.formModel.fields.destinationPorts.validation.rules.push(
			this.PortInputValidationRule({
				minimumPort: 0
			})
		);
 */
		const numberOfDestinationItemsValidation = {
			assert: () => {
				const numberOfIPs = this.state.stateForm.objectIPs?.split(';').filter(s => s !== '').length ?? 0;
				const numberOfURLs = this.state.stateForm.objectURLsFQDNs?.split(';').filter(s => s !== '').length ?? 0;
				const numberOfDestinationItems = numberOfIPs + numberOfURLs;
				const maxNumberOfDestinationItems = 50;
				return numberOfDestinationItems <= maxNumberOfDestinationItems;
			},
			message: "Total Destination URLs and IPs can't be greater than 50"
		}

		this.formModel.addField('objectURLsFQDNs', {
			getValue: () => this.parseMultipleValues(this.state.stateForm.objectURLsFQDNs),
			validation: {
				required: () => !(this.state.stateForm.objectIPs?.trim().length > 0 || this.state.stateForm.nestedObjects?.trim().length),
				rules: [
					{
						assert: () => !this.hasEmptyValues(this.state.stateForm.objectURLsFQDNs),
						message: 'Empty values are not allowed.'
					},
					{
						assert: () => !(this.state.stateForm.objectURLsFQDNs?.includes('https://')
							|| this.state.stateForm.objectURLsFQDNs?.includes('http://')),
						message: 'URL cannot have "http" or "https"'
					},
					{
						assert: (value) => {
							const urls = this.parseMultipleValues(value)?.split(';');
							return urls?.every(url => this.isURLValid(url));
						},
						message: (value) => {
							if (value !== undefined) {
								const invalidAddress = this.parseMultipleValues(value)?.split(';').find(url => !this.isURLValid(url));
								return `${invalidAddress} is an invalid URL.`;
							}
							else return 'Empty values are not allowed.'
						}
					},
					numberOfDestinationItemsValidation
				]
			}
		});

		this.formModel.addField('nestedObjects', {
			getValue: () => this.state.stateForm.nestedObjects,
			validation: {
				required: () => !(this.state.stateForm.objectURLsFQDNs?.trim().length > 0 || this.state.stateForm.objectIPs?.trim().length),
				rules: [
					{
						assert: () => !this.hasEmptyValues(this.state.stateForm.nestedObjects),
						message: 'Empty values are not allowed.'
					}
				]
			}
		});
	}

	// eslint-disable-next-line class-methods-use-this
	getDefaultFormState() {
		return {
			operation: RuleOperation.Add,
			name: '',
			nestedObjectsList: [],
		};
	}

	getRuleCopy() {
        const copy = super.getRuleCopy();
        const nestedObjectsList: OnPremPortOpeningObject[] = copy.nestedObjects
            ? copy.nestedObjects.split(',').filter(n => n.trim()).map((name, idx) => ({
                id: -(idx + 1),
                name: name.trim(),
            } as OnPremPortOpeningObject))
            : [];
        return { ...copy, nestedObjectsList };
    }

    handleNestedObjectSelection = (obj: OnPremPortOpeningObject | null) => {
        if (!obj) return;
        const { nestedObjectsList } = this.state.stateForm;
        if (nestedObjectsList.find(o => o.name === obj.name) || obj.name === this.state.stateForm.name) return;

        const updatedList = [...nestedObjectsList, obj];
        const nestedObjectsString = updatedList.map(o => o.name).join(',');
        this.handleStateFormChange(['nestedObjects', 'nestedObjectsList'] as any, [nestedObjectsString, updatedList] as any);
    };

    handleRemoveNestedObject = (objName: string) => {
        const updatedList = this.state.stateForm.nestedObjectsList.filter(o => o.name !== objName);
        const nestedObjectsString = updatedList.map(o => o.name).join(',');
        this.handleStateFormChange(['nestedObjects', 'nestedObjectsList'] as any, [nestedObjectsString, updatedList] as any);
    };

	resetIPsValidations() {
		this.formModel.fields.sourceIPs.validation.rules = [];
		this.formModel.fields.destinationIPs.validation.rules = [];
	}

	wasEditted() {
		if (this.props.editingRule) {
			return (
				this.props.editingRule.objectIPs != this.stateFormHandler().objectIPs.value
				|| this.props.editingRule.objectURLsFQDNs != this.state.stateForm.objectURLsFQDNs
				|| this.props.editingRule.nestedObjects != this.stateFormHandler().nestedObjects.value
				|| (this.props.editingRule.operation.value == RuleOperation.Add.value && (this.props.editingRule.name != this.state.stateForm.name))
			)
		}
		return true
	}

	render() {
		return (
			<StandardModal
				optionalClass='ruleModal'
				show={this.props.visible}
				title={this.props.isReadOnly ? 'View Object' : !this.isModalForNewRule() ? 'Edit Object' : 'New Object'}
				onDismiss={this.props.onClose as () => any}
				size='em-c-modal--large'
				footer={
					!this.props.isReadOnly &&
					<Grid variant="2up">
						<Grid.Item>
							<Button
								variant="primary"
								disabled={(!this.formModel.isValid() || !this.wasEditted()) && this.state.stateForm.operation.name !== RuleOperation.Remove.name}
								onClick={this.handleRequest}
							>
								{this.props.editingRule ? 'Confirm' : 'Create'}
							</Button>
						</Grid.Item>
						<Grid.Item>
							<Button
								variant="secondary"
								onClick={this.handleCancel}
							>
								Cancel
							</Button>
						</Grid.Item>
					</Grid>
				}
			>
				{this.isModalForNewRule() &&
					<ToggleField
						{...this.stateFormHandler().operation}
						id='operation'
						label="Operation"
						disabled={!this.formCanBeEdited()}
						options={this.props.isReadOnly ?
							[
								RuleOperation.Modify,
								RuleOperation.Remove
							] :
							[
								RuleOperation.Add
							]
						}
					/>
				}
				<Grid variant="2up">
					<Grid.Item>
						<TextInputField
							label="Object Name"
							value={this.state.stateForm.name}
							validation={this.formModel.fields.name.validation}
							onChange={
								(event) => {
									this.handleStateFormChange('name', event.target.value);
								}
							}
							disabled={(this.state.stateForm.operation?.name !== RuleOperation.Add.name && this.state.stateForm.operation?.name !== RuleOperation.Remove.name) || !this.formCanBeEdited()}
						/>
					</Grid.Item>
				</Grid>
				<>
					<Grid variant="2up">
						<Grid.Item>
							<TextAreaField
								value={this.state.stateForm.objectIPs}
								label="Object (IPs / IP Range / CIDR)"
								validation={this.formModel.fields.objectIPs.validation}
								onChange={
									(event) => {
										this.handleStateFormChange('objectIPs', event.target.value);
									}
								}
								note='Multiple values are allowed, separated by semicolons.'
								disabled={!this.formCanBeEdited()}
							/>
						</Grid.Item>
						<Grid.Item>
							<TextAreaField
								label="Object (URLs)"
								value={this.state.stateForm.objectURLsFQDNs}
								validation={this.formModel.fields.objectURLsFQDNs.validation}
								onChange={
									(event) => {
										this.handleStateFormChange('objectURLsFQDNs', event.target.value);
									}
								}
								note='Multiple values are allowed, separated by semicolons.'
								disabled={!this.formCanBeEdited()}
							/>
						</Grid.Item>
					</Grid>
					<Grid variant="2up">
						<Grid.Item>
							<div>
                                <label className="em-c-field__label">Nested Objects</label>
                                {this.formCanBeEdited() && (
                                    <ObjectLookup
                                        label=""
                                        placeholder="Search for nested objects by name..."
                                        value={null}
                                        onChange={this.handleNestedObjectSelection}
                                        note="Type at least 3 characters to search for existing objects."
                                    />
                                )}
                                {this.state.stateForm.nestedObjectsList?.length > 0 && (
                                    <div style={{ marginTop: '8px' }}>
                                        {this.state.stateForm.nestedObjectsList.map((obj) => (
                                            <div
                                                key={obj.name}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    padding: '4px 8px',
                                                    margin: '4px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    backgroundColor: '#f5f5f5',
                                                }}
                                            >
                                                <span style={{ marginRight: '8px' }}>{obj.name}</span>
                                                {this.formCanBeEdited() && (
                                                    <button
                                                        type="button"
                                                        onClick={() => this.handleRemoveNestedObject(obj.name)}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            color: '#cc0000',
                                                            fontWeight: 'bold',
                                                            fontSize: '14px',
                                                        }}
                                                    >
                                                        ✕
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
						</Grid.Item>
					</Grid>
				</>
			</StandardModal>
		);
	}
}